// Four related per-org endpoints:
//
//   POST /{org}/voice_profiles      VoiceProfileCreationRequest
//   POST /{org}/face_profiles       FaceProfileCreationRequest
//   POST /{org}/presenter_profiles  PresenterProfileCreationRequest
//   POST /{org}/contacts            ContactCreationRequest
//
// A Presenter combines a Voice profile + Face profile, and a Contact wraps a
// Presenter with display fields (name, email, phone, calendar). The chain
// is: Voice + Face → Presenter → Contact.

import { useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import type { RequestSpec } from '@/types';
import type { PlaygroundProps } from './index';
import { Field, FormCard, OrgIdRequired, PlaygroundPanel, Select, TextArea, TextInput } from './_shared';
import { VOICE_CATEGORIES, VOICE_PROVIDER_IDS, type VoiceCategory, type VoiceProviderId } from './_constants';

type ProfileKind = 'voice' | 'face' | 'presenter' | 'contact';

export function ProfilesPlayground({ mode }: PlaygroundProps) {
  const { organizationId } = useAuth();
  const [kind, setKind] = useState<ProfileKind>('voice');

  // Common
  const [name, setName] = useState('My voice');
  const [workspaceId, setWorkspaceId] = useState('');

  // Voice
  const [voiceProviderId, setVoiceProviderId] = useState<VoiceProviderId>('high_quality_01');
  const [voiceCategory, setVoiceCategory] = useState<VoiceCategory>('Premade');
  const [providerVoiceId, setProviderVoiceId] = useState('');
  const [voiceSettingsJson, setVoiceSettingsJson] = useState('{}');
  const [voiceTagsJson, setVoiceTagsJson] = useState('');

  // Face
  const [customImageFilename, setCustomImageFilename] = useState('');

  // Presenter
  const [presenterVoiceProfileId, setPresenterVoiceProfileId] = useState('');
  const [presenterFaceProfileId, setPresenterFaceProfileId] = useState('');

  // Contact
  const [firstname, setFirstname] = useState('Jane');
  const [lastname, setLastname] = useState('Doe');
  const [contactPresenterId, setContactPresenterId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [calendar, setCalendar] = useState('');

  const orgSegment = organizationId || '{organization_id}';

  const spec: RequestSpec = useMemo(() => {
    if (kind === 'voice') {
      let settings: Record<string, unknown> = {};
      try {
        settings = voiceSettingsJson.trim() ? JSON.parse(voiceSettingsJson) : {};
      } catch {
        /* invalid JSON — fall back to empty */
      }
      let tags: Record<string, string> | undefined;
      if (voiceTagsJson.trim()) {
        try {
          tags = JSON.parse(voiceTagsJson);
        } catch {
          /* ignore */
        }
      }
      const body: Record<string, unknown> = {
        name,
        provider_id: voiceProviderId,
        category: voiceCategory,
        settings,
      };
      if (voiceCategory !== 'Cloned' && providerVoiceId) body.provider_voice_id = providerVoiceId;
      if (workspaceId) body.workspace_id = workspaceId;
      if (tags) body.tags = tags;
      return { method: 'POST', path: `/media/${orgSegment}/voice_profiles`, body };
    }

    if (kind === 'face') {
      const body: Record<string, unknown> = { name, category: 'Custom' };
      if (customImageFilename) body.custom_image_filename = customImageFilename;
      if (workspaceId) body.workspace_id = workspaceId;
      return { method: 'POST', path: `/media/${orgSegment}/face_profiles`, body };
    }

    if (kind === 'presenter') {
      const body: Record<string, unknown> = {
        name,
        voice_profile_id: presenterVoiceProfileId,
      };
      if (presenterFaceProfileId) body.face_profile_id = presenterFaceProfileId;
      if (workspaceId) body.workspace_id = workspaceId;
      return { method: 'POST', path: `/media/${orgSegment}/presenter_profiles`, body };
    }

    // contact
    const body: Record<string, unknown> = {
      firstname,
      lastname,
      presenter_id: contactPresenterId,
    };
    if (email) body.email = email;
    if (phone) body.phone = phone;
    if (calendar) body.calendar = calendar;
    if (workspaceId) body.workspace_id = workspaceId;
    return { method: 'POST', path: `/media/${orgSegment}/contacts`, body };
  }, [
    kind,
    orgSegment,
    name,
    workspaceId,
    voiceProviderId,
    voiceCategory,
    providerVoiceId,
    voiceSettingsJson,
    voiceTagsJson,
    customImageFilename,
    presenterVoiceProfileId,
    presenterFaceProfileId,
    firstname,
    lastname,
    contactPresenterId,
    email,
    phone,
    calendar,
  ]);

  return (
    <PlaygroundPanel
      title="Voice / Face / Presenter / Contact"
      description="The chain: a Voice profile + a Face profile combine into a Presenter; a Contact wraps a Presenter with name, email, phone, and calendar URL — the Contact ID is what you reference from a Project."
      spec={spec}
      mode={mode}
    >
      <OrgIdRequired organizationId={organizationId} />
      <FormCard>
        <Field label="What to create">
          <Select value={kind} onChange={(e) => setKind(e.target.value as ProfileKind)}>
            <option value="voice">Voice profile</option>
            <option value="face">Face profile</option>
            <option value="presenter">Presenter (voice + face)</option>
            <option value="contact">Contact (wraps a Presenter)</option>
          </Select>
        </Field>

        {kind !== 'contact' && (
          <Field label="Name" hint="Required">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
        )}

        {/* ========================== VOICE ========================== */}
        {kind === 'voice' && (
          <>
            <div className="lp-grid lp-grid-cols-2 lp-gap-3">
              <Field label="provider_id" hint="VoiceProviderId">
                <Select value={voiceProviderId} onChange={(e) => setVoiceProviderId(e.target.value as VoiceProviderId)}>
                  {VOICE_PROVIDER_IDS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Select>
              </Field>
              <Field label="category" hint="VoiceCategory">
                <Select value={voiceCategory} onChange={(e) => setVoiceCategory(e.target.value as VoiceCategory)}>
                  {VOICE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </Field>
            </div>
            {voiceCategory !== 'Cloned' && (
              <Field label="provider_voice_id" hint="Required for Premade / Professional">
                <TextInput value={providerVoiceId} onChange={(e) => setProviderVoiceId(e.target.value)} placeholder="e.g. en-US-female-3" />
              </Field>
            )}
            <Field label="settings (JSON)" hint="Provider-specific settings — opaque to the API">
              <TextArea
                rows={3}
                value={voiceSettingsJson}
                onChange={(e) => setVoiceSettingsJson(e.target.value)}
                className="lp-font-mono lp-text-xs"
                placeholder='{"speed": 1.0, "pitch": 0}'
              />
            </Field>
            <Field label="tags (JSON object)" hint='{"key": "value"} pairs — optional'>
              <TextArea
                rows={2}
                value={voiceTagsJson}
                onChange={(e) => setVoiceTagsJson(e.target.value)}
                className="lp-font-mono lp-text-xs"
                placeholder='{"language": "en"}'
              />
            </Field>
          </>
        )}

        {/* ========================== FACE ========================== */}
        {kind === 'face' && (
          <Field label="custom_image_filename" hint="Filename of the uploaded face image asset">
            <TextInput value={customImageFilename} onChange={(e) => setCustomImageFilename(e.target.value)} placeholder="face_jane.png" />
          </Field>
        )}

        {/* ========================== PRESENTER ========================== */}
        {kind === 'presenter' && (
          <>
            <Field label="voice_profile_id" hint="Required">
              <TextInput value={presenterVoiceProfileId} onChange={(e) => setPresenterVoiceProfileId(e.target.value)} placeholder="vp_…" required />
            </Field>
            <Field label="face_profile_id" hint="Optional — voice-only presenter is allowed">
              <TextInput value={presenterFaceProfileId} onChange={(e) => setPresenterFaceProfileId(e.target.value)} placeholder="fp_…" />
            </Field>
          </>
        )}

        {/* ========================== CONTACT ========================== */}
        {kind === 'contact' && (
          <>
            <div className="lp-grid lp-grid-cols-2 lp-gap-3">
              <Field label="firstname" hint="Required">
                <TextInput value={firstname} onChange={(e) => setFirstname(e.target.value)} required />
              </Field>
              <Field label="lastname" hint="Required">
                <TextInput value={lastname} onChange={(e) => setLastname(e.target.value)} required />
              </Field>
            </div>
            <Field label="presenter_id" hint="Required — the Presenter this contact embodies">
              <TextInput value={contactPresenterId} onChange={(e) => setContactPresenterId(e.target.value)} placeholder="pp_…" required />
            </Field>
            <div className="lp-grid lp-grid-cols-1 sm:lp-grid-cols-3 lp-gap-3">
              <Field label="email">
                <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@acme.com" />
              </Field>
              <Field label="phone">
                <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555…" />
              </Field>
              <Field label="calendar" hint="Booking URL">
                <TextInput value={calendar} onChange={(e) => setCalendar(e.target.value)} placeholder="https://cal.com/…" />
              </Field>
            </div>
          </>
        )}

        <Field label="workspace_id" hint="Optional, applies to all four entities">
          <TextInput value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} placeholder="ws_…" />
        </Field>
      </FormCard>
    </PlaygroundPanel>
  );
}
